import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Badge } from '@/components/ui/Badge'
import { KpiCard } from '@/components/ui/KpiCard'
import { Notice } from '@/components/ui/Notice'
import { Panel } from '@/components/ui/Panel'
import { formatMoney, formatPercent, sumMoney } from '@/lib/format'

import { useDrillDown } from '../drilldown/DrillDownContext'
import { useCarriedSearch } from '../hooks/usePeriod'
import { labelWithAsOf, monthRange, periodStartLabel } from '../lib/calendar'
import { isPaceApplicable, isPeriodComplete, paceStatus, pctOfBudget, projectedTotal, varianceWords } from '../lib/pace'
import { actualLabel, periodNoun, periodShort } from '../lib/periodText'
import { useRunwayModel } from '../RunwayContext'
import { type BulletRowData, BulletRows } from './BulletRows'
import { ConcentrationPanel } from './ConcentrationPanel'
import { ConfigNotices } from './ConfigNotices'
import layout from './Layout.module.css'
import { type NotableRowData, NotableTable } from './NotableTable'
import { PacePanel } from './PacePanel'
import { PageHead } from './PageHead'
import { VarianceTag } from './VarianceTag'
import { VendorRows } from './VendorRows'

function rowActionLabel(row: BulletRowData): string {
  return `Open ${row.name}`
}

export function OverviewPage() {
  const { runway, calendar, pacePct, deptName } = useRunwayModel()
  const { openDrillDown } = useDrillDown()
  const navigate = useNavigate()
  const search = useCarriedSearch()
  const order = runway.department_order
  const { period } = runway
  const quarterShort = periodShort(period)
  const label = period.label
  const months = monthRange(calendar)

  const complete = isPeriodComplete(runway)
  const paceApplies = isPaceApplicable(runway)
  const outside = runway.outside_tiles

  const summary = useMemo(() => {
    let unbudgeted = 0
    const rows = order.flatMap((code) => {
      const d = runway.departments[code]
      if (!d) return []
      if (d.quarter_budget <= 0) unbudgeted += 1
      const used = pctOfBudget(d.qtd_actual, d.quarter_budget)
      return [{ code, d, used, status: paceStatus(used, pacePct), variance: d.qtd_actual - d.quarter_budget }]
    })
    // Totals in cents: the KPI equals the sum of the department figures shown below it.
    const actual = sumMoney(rows, (r) => r.d.qtd_actual)
    const budget = sumMoney(rows, (r) => r.d.quarter_budget)
    // Highest % used first; departments without a budget (no % to rank) go last.
    rows.sort((a, b) => Number(a.used === null) - Number(b.used === null) || (b.used ?? 0) - (a.used ?? 0))
    // Rows are sorted by % used, highest first: the first budgeted one is the most over pace.
    const top = rows.filter((r) => r.used !== null)[0] ?? null
    return { actual, budget, unbudgeted, rows, top }
  }, [order, runway.departments, pacePct])

  const projected = projectedTotal(summary.actual, runway.days_elapsed_in_quarter, runway.days_total_in_quarter)
  const companyVariance = summary.actual - summary.budget
  // "Over pace": more of the budget used than of the quarter elapsed.
  const overPace = summary.top !== null && summary.top.status === 'bad' ? summary.top : null

  const bulletRows = useMemo<BulletRowData[]>(
    () =>
      summary.rows.map((r) => ({
        key: r.code,
        name: r.d.name,
        actual: r.d.qtd_actual,
        budget: r.d.quarter_budget,
        status: r.status,
        figures:
          r.used === null ? (
            <>
              no budget <span className="text-faint">· {formatMoney(r.d.qtd_actual, { compact: true })} spent</span>
            </>
          ) : (
            <>
              {formatPercent(r.used)} used{' '}
              <span className="text-faint">of {formatMoney(r.d.quarter_budget, { compact: true })}</span> ·{' '}
              <VarianceTag variance={r.variance} />
            </>
          ),
      })),
    [summary.rows],
  )

  const openDepartment = useCallback(
    (code: string) => navigate({ pathname: `/departments/${encodeURIComponent(code)}`, search }),
    [navigate, search],
  )
  const drillVendor = useCallback(
    (who: string) => openDrillDown({ dept: null, filter: { type: 'vendor', value: who }, label: who }),
    [openDrillDown],
  )
  const drillNotable = useCallback(
    (row: NotableRowData) =>
      openDrillDown({ dept: row.dept, filter: { type: 'acct', value: row.acct }, label: `${row.acct} — ${row.title}` }),
    [openDrillDown],
  )

  const notableRows = runway.company.notable

  return (
    <div className={layout.view}>
      <PageHead
        title="All Departments"
        sub={`${period.is_live ? 'Live expenses' : complete ? `Closed ${periodNoun(period)}: expenses` : 'Expenses'} vs. ${label} budget (${months}) · as of ${runway.as_of}`}
        badge={<Badge tone="neutral">{order.length} departments</Badge>}
      />
      <ConfigNotices runway={runway} />
      <Notice>
        {runway.budget === null ? (
          <>
            <b>No budget on file for {label}</b> — no Sage budget covers these months; every department shows &ldquo;no
            budget&rdquo;.{' '}
          </>
        ) : null}
        {runway.budget !== null && summary.unbudgeted > 0 ? (
          <>
            {summary.unbudgeted} of {order.length} departments have <b>no {label} budget on file</b> (marked
            &ldquo;n/b&rdquo; in the sidebar).{' '}
          </>
        ) : null}
        {complete ? (
          <>
            <b>{label} is complete</b> — figures are final: each department is compared with its full budget (over or
            under), with no projection.{' '}
          </>
        ) : (
          <>
            <b>
              {formatPercent(pacePct)} of {label} has elapsed
            </b>{' '}
            (as of {runway.as_of}) — that&rsquo;s the bar every department below is measured against, not 100%.{' '}
          </>
        )}
        Only operating-expense accounts count; escrow, AR and loan-participation postings are balance-sheet movements
        and are excluded.
      </Notice>

      <div className={layout.kpiRow}>
        <KpiCard
          label={actualLabel(period)}
          value={formatMoney(summary.actual)}
          sub={
            outside.count > 0
              ? `Sum of the ${order.length} departments shown · excludes ${formatMoney(outside.actual, { compact: true })} outside`
              : `${order.length} departments · ${periodStartLabel(calendar)} – ${runway.as_of}`
          }
        />
        <KpiCard
          label={`${label} Budget`}
          value={formatMoney(summary.budget)}
          sub={outside.count > 0 ? `Full ${months} plan, departments shown` : `Full ${months} plan, combined`}
        />
        {complete ? (
          <KpiCard
            label={`${quarterShort} Final vs. Budget`}
            value={summary.budget > 0 ? formatMoney(Math.abs(companyVariance)) : '—'}
            tone={summary.budget > 0 ? (companyVariance <= 0 ? 'good' : 'bad') : null}
            sub={summary.budget > 0 ? `${varianceWords(companyVariance)} budget, final` : 'no budget on file'}
          />
        ) : paceApplies ? (
          <KpiCard
            label={`Projected ${quarterShort} Total`}
            value={formatMoney(projected)}
            tone={summary.budget > 0 ? (projected <= summary.budget ? 'good' : 'bad') : null}
            sub="at company-wide run-rate"
          />
        ) : (
          <KpiCard label="Projected Total" value="—" sub="needs adjoining months" />
        )}
        <KpiCard
          label={complete ? 'Most Over Budget' : 'Most Over Pace'}
          textValue
          value={
            overPace ? overPace.d.name : summary.top ? (complete ? 'None over budget' : 'All on or under pace') : '—'
          }
          sub={
            overPace?.used != null
              ? complete
                ? `${formatPercent(overPace.used)} of ${quarterShort} budget used`
                : `${formatPercent(overPace.used)} used vs. ${formatPercent(pacePct)} of the ${periodNoun(period)} elapsed`
              : summary.top
                ? complete
                  ? 'every budgeted department finished at or under budget'
                  : `every budgeted department is at or under the ${formatPercent(pacePct)} pace`
                : 'no department has a budget'
          }
        />
      </div>

      <PacePanel
        title={`Company-wide spending pace — ${labelWithAsOf(runway)}`}
        codes={order}
        budget={summary.budget}
      />

      <Panel
        title={complete ? 'Ranked by budget used — highest first' : 'Ranked by pace — most over pace first'}
        aside={`Bar = % of ${quarterShort} budget used · tick = full budget · click a row to open that department`}
      >
        <BulletRows rows={bulletRows} onActivate={openDepartment} actionLabel={rowActionLabel} />
      </Panel>

      <div className={layout.grid2}>
        <Panel title="Where the money goes" aside="Top vendors company-wide · click for detail">
          <VendorRows payees={runway.company.top_payees} onActivate={drillVendor} />
        </Panel>
        <Panel title="Vendor concentration" aside="How spread out is spend?">
          <ConcentrationPanel
            concentrationPct={runway.company.concentration_pct}
            vendorCount={runway.company.vendor_count}
            txnCount={runway.company.txn_count}
            departmentCount={order.length}
            quarterShort={quarterShort}
          />
        </Panel>
      </div>

      <Panel
        title="Notable transactions"
        aside={`Largest single vendor transactions company-wide in ${label} · credits in parentheses`}
      >
        <NotableTable rows={notableRows} showDept deptName={deptName} onActivate={drillNotable} />
      </Panel>
    </div>
  )
}
